import React, { useState, useEffect } from 'react';
import { Footer } from '../../components/Footer';
import { NewDashboardHeader } from '../../components/NewDashboardHeader';
import { CIEN_CUADRAS_BLUE } from '../../constants';
import { ToastNotification } from '../../components/ui/ToastNotification';
import { useToast } from '../../hooks/useToast';
import { SignInForm } from './components/SignInForm';
import { RegisterStepEmail } from './components/RegisterStepEmail';
import { RegisterStepPassword } from './components/RegisterStepPassword';
import { GoogleAuthButton } from './components/GoogleAuthButton';
import { PasswordResetSection } from './components/PasswordResetSection';
import { LoginViewProps } from './types';

export const LoginView: React.FC<LoginViewProps> = ({
  onGoogleSignIn,
  onEmailPasswordSignIn,
  onEmailPasswordSignUp,
  onPasswordReset,
  loginError,
  passwordResetMessage,
  aiInitializationError,
  isGoogleLoading,
  isEmailLoading,
  isGoogleLoginEnabled,
  isEmailPasswordLoginEnabled,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [currentAction, setCurrentAction] = useState<'signIn' | 'signUp'>('signIn');
  const [signUpStep, setSignUpStep] = useState<1 | 2>(1);

  const { toast, showError, showSuccess, hideToast } = useToast();

  useEffect(() => {
    if (loginError) showError(loginError);
  }, [loginError, showError]);

  useEffect(() => {
    if (passwordResetMessage) showSuccess(passwordResetMessage);
  }, [passwordResetMessage, showSuccess]);

  useEffect(() => {
    if (aiInitializationError) showError(aiInitializationError);
  }, [aiInitializationError, showError]);

  const handleSignInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await onEmailPasswordSignIn(email, password);
    if (!ok && !loginError) showError('Error al iniciar sesión. Verifica tus credenciales.');
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      showError('Las contraseñas no coinciden.');
      return;
    }
    const ok = await onEmailPasswordSignUp(email, password, displayName);
    if (!ok && !loginError) showError('Error al completar el registro.');
  };

  const handleGoogleClick = async () => {
    try {
      await onGoogleSignIn();
    } catch (err: any) {
      showError(err?.message || 'Error al conectar con Google');
    }
  };

  const handleResetPasswordAction = async (targetEmail: string) => {
    const success = await onPasswordReset(targetEmail);
    if (success) {
      showSuccess(`Se ha enviado un correo a ${targetEmail} para restablecer tu contraseña.`);
      setShowPasswordReset(false);
    }
    return success;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
      <ToastNotification toast={toast} onClose={hideToast} duration={3000} />
      <NewDashboardHeader />
      <main className="flex-grow flex flex-col items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100">
          <h2 className="text-2xl font-black text-center mb-6" style={{ color: CIEN_CUADRAS_BLUE }}>
            {showPasswordReset ? 'Recuperar Cuenta' : currentAction === 'signIn' ? 'Iniciar Sesión' : 'Crear Cuenta de Broker'}
          </h2>

          {!showPasswordReset ? (
            <>
              {currentAction === 'signIn' ? (
                isEmailPasswordLoginEnabled && (
                  <SignInForm
                    email={email} setEmail={setEmail} pass={password} setPass={setPassword}
                    onSubmit={handleSignInSubmit} onForgotPassword={() => setShowPasswordReset(true)}
                    onSwitchToSignUp={() => { setCurrentAction('signUp'); setSignUpStep(1); }}
                    isLoading={isEmailLoading}
                  />
                )
              ) : signUpStep === 1 ? (
                <RegisterStepEmail
                  email={email} setEmail={setEmail} onNext={() => setSignUpStep(2)}
                  onEmailExists={(msg) => showError(msg)} onSwitchToSignIn={() => setCurrentAction('signIn')}
                  isLoading={isEmailLoading}
                />
              ) : (
                <RegisterStepPassword
                  email={email} pass={password} setPass={setPassword}
                  confirmPass={confirmPassword} setConfirmPass={setConfirmPassword}
                  displayName={displayName} setDisplayName={setDisplayName}
                  onBack={() => setSignUpStep(1)} onSubmit={handleSignUpSubmit}
                  isLoading={isEmailLoading}
                />
              )}

              {currentAction === 'signIn' && isGoogleLoginEnabled && (
                <GoogleAuthButton onGoogleSignIn={handleGoogleClick} isLoading={isGoogleLoading} />
              )}
            </>
          ) : (
            <PasswordResetSection
              onPasswordReset={handleResetPasswordAction}
              onCancel={() => setShowPasswordReset(false)}
              isLoading={isEmailLoading}
            />
          )}

          <p className="mt-6 text-center text-xs text-gray-400">
            Al continuar, aceptas los Términos de Servicio y la Política de Privacidad de CienCuadras.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};
